/** Pure purchase gating kept separate from the browser and Firebase. */
export interface PurchaseReceipt {
  transaction_id: string;
  value: number;
  currency: string;
  livemode: boolean;
}

export type PurchaseReceiptResult =
  | { status: 'paid'; receipt: PurchaseReceipt }
  | { status: 'pending' | 'ineligible'; receipt: null };

export type AdsConsent = 'granted' | 'denied' | 'unset';
export interface AdsConfiguration {
  id: string;
  label: string;
  mode: 'production' | 'test';
}

export function resolveAdsConfiguration(
  env: Record<string, string | boolean | undefined>,
  hostname: string,
): AdsConfiguration | null {
  const productionHost = hostname === 'www.jampackd.store' || hostname === 'jampackd.store';
  const mode = env.VITE_GOOGLE_ADS_TRACKING_MODE;
  const production = mode === 'production' && env.PROD === true &&
    env.VITE_APP_ENVIRONMENT === 'production' && productionHost;
  const test = mode === 'test' && !productionHost && env.VITE_APP_ENVIRONMENT !== 'production';
  if (!production && !test) return null;
  const id = String(production ? env.VITE_GOOGLE_ADS_ID || '' : env.VITE_GOOGLE_ADS_TEST_ID || '');
  const label = String(production ? env.VITE_GOOGLE_ADS_CONVERSION_LABEL || '' : env.VITE_GOOGLE_ADS_TEST_CONVERSION_LABEL || '');
  if (!/^AW-\d+$/.test(id) || !/^[\w-]+$/.test(label)) return null;
  // Require the live destination for comparison, so missing preview env values
  // cannot silently allow test Stripe payments into the production action.
  if (test) {
    const productionId = String(env.VITE_GOOGLE_ADS_ID || '');
    const productionLabel = String(env.VITE_GOOGLE_ADS_CONVERSION_LABEL || '');
    if (!/^AW-\d+$/.test(productionId) || !/^[\w-]+$/.test(productionLabel)) return null;
    if (`${id}/${label}` === `${productionId}/${productionLabel}`) return null;
  }
  return { id, label, mode: production ? 'production' : 'test' };
}

export function isEligibleReceipt(receipt: PurchaseReceipt, config: AdsConfiguration): boolean {
  return Boolean(receipt && /^(pi|in)_[a-zA-Z0-9]+$/.test(receipt.transaction_id) &&
    Number.isFinite(receipt.value) && receipt.value > 0 && /^[A-Z]{3}$/.test(receipt.currency) &&
    typeof receipt.livemode === 'boolean' && receipt.livemode === (config.mode === 'production'));
}

interface TrackerDependencies {
  config: AdsConfiguration;
  consent: () => AdsConsent;
  loadTag: () => Promise<boolean>;
  send: (parameters: Record<string, unknown>) => void;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
}

export function createPurchaseTracker(dependencies: TrackerDependencies) {
  const { config, storage } = dependencies;
  const pending = new Map<string, Promise<boolean>>();
  const sent = new Set<string>();
  return (receipt: PurchaseReceipt): Promise<boolean> => {
    if (dependencies.consent() !== 'granted' || !isEligibleReceipt(receipt, config)) return Promise.resolve(false);
    const key = `jampackd:google-purchase:${config.id}/${config.label}:${receipt.transaction_id}`;
    if (sent.has(key)) return Promise.resolve(false);
    try { if (storage.getItem(key)) return Promise.resolve(false); } catch { /* Storage can be blocked. */ }
    if (pending.has(key)) return pending.get(key)!;
    const operation = (async () => {
      try {
        if (!await dependencies.loadTag() || dependencies.consent() !== 'granted') return false;
        dependencies.send({
          send_to: `${config.id}/${config.label}`,
          transaction_id: receipt.transaction_id,
          value: receipt.value,
          currency: receipt.currency,
          allow_ad_personalization_signals: false,
        });
        sent.add(key);
        try { storage.setItem(key, '1'); } catch { /* Google also deduplicates the stable ID. */ }
        return true;
      } catch { return false; }
    })();
    pending.set(key, operation);
    void operation.finally(() => pending.delete(key));
    return operation;
  };
}

/** Only poll pending receipts, with a bounded retry window and cancellation. */
export async function waitForPaidReceipt(
  fetchReceipt: () => Promise<PurchaseReceiptResult>,
  options: { signal: AbortSignal; pause: (ms: number) => Promise<void>; attempts?: number },
): Promise<PurchaseReceipt | null> {
  for (let attempt = 0; attempt < (options.attempts ?? 7); attempt++) {
    if (options.signal.aborted) return null;
    const result = await fetchReceipt();
    if (options.signal.aborted) return null;
    if (result.status === 'paid') return result.receipt;
    if (result.status !== 'pending' || attempt === (options.attempts ?? 7) - 1) return null;
    await options.pause(5000);
  }
  return null;
}
